import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { AdminAPIs } from "../../api/network/adminApiServices";
import { store } from "../../api/store/store";
import { ASSET_MAP_STATUS } from "../../api/types/Vehicle";
import { mapVehicleIconWrapped } from "../../assets/svg/vehicle-wrapped";
import { APP_CONFIG } from "../../constants/constants";
import { TMapType } from "../../types/map";
import { CustomRenderer } from "../../utils/map";
import { getAccountMapIconSizeMultiplierNoHook } from "../../utils/user";
import { createSelectedEventPulse } from "../map-asset/multiDay/eventPinFocus";
import { getVehicleUUID, mapInfoBoxContentWDriver } from "./common";
import {
  TAssetLocationDataPoint,
  TDataPoint,
  TMapOperations,
  TMapOperationsProps,
  TMapUpdatesHandler,
  TMPushpin,
} from "./type";

const fetchDriverHtml = async (
  dataPoint: TDataPoint | TAssetLocationDataPoint,
  props: TMapOperationsProps
) => {
  if (!dataPoint?.accountid || !dataPoint?.deviceid) {
    return mapInfoBoxContentWDriver(dataPoint, null, false);
  }

  try {
    const accountSettings = props.accountSettings;

    let driverName: string | null = null;
    if (accountSettings?.infoboxdriver === 1) {
      const driverResult = await store.dispatch(
        AdminAPIs.endpoints.getAssignedDriver.initiate({
          account_id: dataPoint.accountid,
          device_id: dataPoint.deviceid,
        })
      );
      if (
        !driverResult.error &&
        driverResult.data &&
        driverResult.data.driverid
      ) {
        driverName = [driverResult.data.description, driverResult.data.driverid]
          .filter((item) => !!item)
          .join(" | ");
      }
    }

    return mapInfoBoxContentWDriver(
      dataPoint,
      driverName,
      false,
      accountSettings
    );
  } catch (error) {
    console.error("Error in fetchDriverHtml:", error);
    return mapInfoBoxContentWDriver(dataPoint, null, false);
  }
};

export const mapOperations: TMapOperations = (props, checkedVehicles) => {
  const google = (window as any).google;
  // (window as any).map = props.mapRef.current;

  // create a namespace for map objects
  props.mapRef.current.objects = {
    mPushpins: [],
    mClusterLayer: null,
  };

  const renderMapObjects = async () => {
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
    const map = props.mapRef.current.map;
    const parser = new DOMParser();

    const alwaysOpen = props.accountSettings?.infoboxalwaysopen ?? 0;

    // create pushpins
    const markerArr: any[] = []; // Create a marker array to be added to cluster
    props.dataPoints.forEach((dataPoint) => {
      let angle = mapVehicleStateIconAngle(dataPoint);
      if (angle !== 0 && APP_CONFIG.DEBUG.MAPS) {
        // temporary polyline for testing
        // const thisPolyline = new google.maps.Polyline(
        //   [
        //     new google.maps.Location(dataPoint.coords[0], dataPoint.coords[1]),
        //     new google.maps.Location(dataPoint.coords[2], dataPont.coords[3])
        //   ],
        //   {
        //     strokeColor: '#0099DD',
        //     strokeThickness: 2,
        //   }
        // );
        // props.mapRef.current.map.entities.push(thisPolyline);
      }
      // create a pushpin
      if (
        (!!!(dataPoint as TDataPoint)?.lastvalidlatitude &&
          !!!(dataPoint as TAssetLocationDataPoint)?.eventdata?.latitude) ||
        (!!!(dataPoint as TDataPoint)?.lastvalidlongitude &&
          !!!(dataPoint as TAssetLocationDataPoint)?.eventdata?.longitude)
      ) {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log(
            "Failed to drop pushpin at",
            (dataPoint as TDataPoint)?.lastvalidlatitude ??
              (dataPoint as TAssetLocationDataPoint)?.eventdata?.latitude,
            (dataPoint as TDataPoint)?.lastvalidlongitude ??
              (dataPoint as TAssetLocationDataPoint)?.eventdata?.longitude
          );
        return;
      }
      // marker background highlight marker
      const size =
        APP_CONFIG.MAPS.VEHICLE_ICON_SIZE *
        getAccountMapIconSizeMultiplierNoHook();
      const pinSvgString = `<svg height="${size}" width="${size}" xmlns="http://www.w3.org/2000/svg"><circle r="${size / 2}" cx="${size / 2}" cy="${size / 2}" fill="white" /></svg>`;
      const bgHlMrkrPinSvg = parser.parseFromString(
        pinSvgString,
        "image/svg+xml"
      ).documentElement;
      const bgHighlightMarker = new AdvancedMarkerElement({
        map: null, // map
        position: {
          lat:
            (dataPoint as TDataPoint)?.lastvalidlatitude ??
            (dataPoint as TAssetLocationDataPoint)?.eventdata?.latitude,
          lng:
            (dataPoint as TDataPoint)?.lastvalidlongitude ??
            (dataPoint as TAssetLocationDataPoint)?.eventdata?.longitude,
        },
        content: bgHlMrkrPinSvg,
        title: dataPoint.deviceid, // used to identify which markers fall under a cluster and show infobox accordingly
        zIndex: 1,
      });
      // actual marker
      const pinSvg = parser.parseFromString(
        mapVehicleIconWrapped(
          mapVehicleStateIconSlug(dataPoint),
          false,
          (dataPoint as TAssetLocationDataPoint)?.asset_map_status ===
            ASSET_MAP_STATUS.IGNITION_OFF
            ? 45 // TODO: need to fix why 45 needs to be provided
            : angle,
          APP_CONFIG.MAPS.VEHICLE_ICON_SIZE *
            getAccountMapIconSizeMultiplierNoHook(),
          false, // onlyIcon = false
          false, // highlightIcon = false
          dataPoint,
          props.isAssetImageEnabled
        ),
        "image/svg+xml"
      ).documentElement;
      const highlightedPinSvg = parser.parseFromString(
        mapVehicleIconWrapped(
          mapVehicleStateIconSlug(dataPoint),
          false,
          (dataPoint as TAssetLocationDataPoint)?.asset_map_status ===
            ASSET_MAP_STATUS.IGNITION_OFF
            ? 45 // TODO: need to fix why 45 needs to be provided
            : angle,
          APP_CONFIG.MAPS.VEHICLE_ICON_SIZE *
            getAccountMapIconSizeMultiplierNoHook(), // size
          false, // onlyIcon = false
          true, // highlightIcon = true
          dataPoint,
          props.isAssetImageEnabled
        ),
        "image/svg+xml"
      ).documentElement;
      const marker = new AdvancedMarkerElement({
        map: props.mapLayerOptions?.clustering ? null : map,
        position: {
          lat:
            (dataPoint as TDataPoint)?.lastvalidlatitude ??
            (dataPoint as TAssetLocationDataPoint)?.eventdata?.latitude,
          lng:
            (dataPoint as TDataPoint)?.lastvalidlongitude ??
            (dataPoint as TAssetLocationDataPoint)?.eventdata?.longitude,
        },
        content: pinSvg,
        title: dataPoint.deviceid,
        zIndex: 2,
      });

      marker.addEventListener("click", () => {
        props.onDataPointPushpinClick?.(dataPoint);
      });

      const infobox = new google.maps.InfoWindow({
        maxWidth: 300,
        headerDisabled: true,
        zIndex: APP_CONFIG.MAPS.INFOBOX_ZINDEX,
      });

      marker.addEventListener("mouseover", () => {
        const initialHtml = mapInfoBoxContentWDriver(
          dataPoint,
          null,
          false,
          props.accountSettings
        );
        infobox.setContent(initialHtml);
        infobox.open({
          anchor: marker,
          map: map,
        });

        const showDriver = props.accountSettings?.infoboxdriver ?? 0;
        if (showDriver === 1) {
          fetchDriverHtml(dataPoint, props).then((fullHtml) => {
            if (infobox.getAnchor() === marker) {
              infobox.setContent(fullHtml);
            }
          });
        }
      });

      marker.addEventListener("mouseout", () => {
        if (alwaysOpen !== 1) {
          infobox.close();
        }
      });
      //   map.Events.addHandler(thisPushpinInfoBox, "click", (e: any) => {
      //     props.onDataPointPushpinClick?.(dataPoint);
      //   });

      const thisPushpinObject: TMPushpin = {
        id: getVehicleUUID(dataPoint),
        deviceid: (dataPoint as TDataPoint)?.deviceid,
        pushpin: marker,
        // pushpinHighlightMarker: bgHighlightMarker,
        markerSvgs: {
          regular: pinSvg,
          highlighted: highlightedPinSvg,
        },
        infobox: infobox,
        dataPoint: dataPoint,
      };
      //   // props.mapRef.current.map.entities.push(thisPushpin); // add the pushpin to the map
      props.mapRef.current.objects.mPushpins.push(thisPushpinObject);
      //   if (APP_CONFIG.DEBUG.MAPS)
      //     console.log(
      //       "Dropping pushpin at",
      //       (dataPoint as TDataPoint)?.lastvalidlatitude ??
      //         (dataPoint as TAssetLocationDataPoint)?.eventdata?.latitude,
      //       (dataPoint as TDataPoint)?.lastvalidlongitude ??
      //         (dataPoint as TAssetLocationDataPoint)?.eventdata?.longitude
      //     );

      markerArr.push(marker);
    });

    if (props.mapLayerOptions?.clustering) {
      (window as any).mapAllClusterableMarkers = markerArr;
      (window as any).mapClusterLayer = new MarkerClusterer({
        markers: markerArr,
        map: map,
        renderer: new CustomRenderer(
          props.dataPoints as TDataPoint[],
          "assets"
        ),
      });
    }

    if (alwaysOpen === 1) {
      setTimeout(() => {
        if (!props.mapRef.current?.objects?.mPushpins) {
          console.log("Pushpins not available after timeout");
          return;
        }

        props.mapRef.current.objects.mPushpins.forEach(
          (pushpinObject: TMPushpin) => {
            const marker = pushpinObject.pushpin;

            if (!marker.map) {
              return;
            }

            const dataPoint = pushpinObject.dataPoint;
            if (!dataPoint) {
              return;
            }

            const initialHtml = mapInfoBoxContentWDriver(
              dataPoint,
              null,
              false,
              props.accountSettings
            );

            pushpinObject.infobox.setContent(initialHtml);

            pushpinObject.infobox.open({
              anchor: marker,
              map: map,
              shouldFocus: false,
            });

            const showDriver = props.accountSettings?.infoboxdriver ?? 0;
            if (showDriver === 1) {
              fetchDriverHtml(dataPoint, props).then((fullHtml) => {
                if (pushpinObject.infobox.getAnchor() === marker) {
                  pushpinObject.infobox.setContent(fullHtml);
                }
              });
            }
          }
        );
      }, 1500);
    }

    // props.mapRef.current.objects.mClusterLayer = new google.maps.ClusterLayer(
    //   props.mapRef.current.objects.mPushpins.map(
    //     (pushpinObject) => pushpinObject.pushpin
    //   ),
    //   {
    //     clusteredPinCallback: (cluster: any) => {
    //       customClusteredPinCallback?.(
    //         cluster,
    //         props?.saveLastView,
    //         props?.mapRef
    //       );
    //     },
    //   }
    // );
    // props.mapRef.current.map.layers.insert(
    //   props.mapRef.current.objects.mClusterLayer
    // );
    // props.mapRef.current.objects.mClusterLayer.setOptions({
    //   clusteringEnabled: props.mapLayerOptions?.clustering,
    //   // visible: true,
    //   // gridSize: APP_CONFIG.MAPS.CLUSTER_GRID_SIZE,
    //   // clusterPlacementType: google.maps.ClusterPlacementType.Grid,
    //   // icon: mapVehicleIconWrapped('cluster', false, 0),
    //   // iconTemplate: mapVehicleIconWrapped('cluster', false, 0),
    //   // textOffset: new google.maps.Point(0, 0),
    //   // textTemplate: '{pointCount}',
    //   // textOffset: new google.maps.Point(0, 0),
    //   // textOptions: {
    //   //   visible: true,
    //   //   color: '#FFFFFF',
    //   //   fontSize: 12,
    //   //   fontFamily: 'Segoe UI, sans-serif',
    //   //   textAnchor: google.maps.Anchor.Center,
    //   // },
    // });

    // center the map to the polygon
    setTimeout(() => {
      // the caller is placing the camera itself - see map-fleet's
      // handleMapReady restoring a selection after a refresh. Both used to
      // land about a second after the map came back and whichever ran last
      // won, so the view sometimes snapped back to where the asset had been
      // before the refresh instead of following it.
      if (props.skipInitialCamera) return;
      // override map view if provided
      if (props.overrideMapView) {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log("[fleetMap] overrideMapView", props.overrideMapView);
        props.mapRef.current.map.setOptions({
          center: !!props.overrideMapView.center
            ? {
                lat: props.overrideMapView.center.latitude,
                lng: props.overrideMapView.center.longitude,
              }
            : undefined,
          zoom: props.overrideMapView.zoom,
          animate: true,
        });
        return;
      }
      // otherwise center to data points
      mapCenterToDataPoints(props, checkedVehicles);
    }, 1000);

    // zoom event listener
    // map.Events.addListener(
    //   props.mapRef.current.map,
    //   "viewchangeend",
    //   (e: any) => {
    //     const mapTypeId = props?.mapRef?.current?.map?.getMapTypeId();
    //     const mapMode = props?.mapRef?.current?.map?.getMode()?.mapModeType;
    //     if (APP_CONFIG.DEBUG.MAPS)
    //       console.log(
    //         `== VIEW CHANGED [id: ${mapTypeId}] [mode: ${mapMode}]`,
    //         e
    //       );
    //     updateInfoboxesVisibility(props);
    //     if (mapMode === APP_CONFIG.MAPS.STREETSIDE_MAP_MODE) {
    //       if (APP_CONFIG.DEBUG.MAPS)
    //         console.info("==== STREETVIEW OPEN, HIDE PUSH PINS");
    //       updateEntitiesOnMapTypeChange(
    //         props,
    //         APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
    //           .type as TMapType
    //       ); // to hide pushpins on streetside view open
    //     } else {
    //       if (
    //         mapTypeId ===
    //         APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].id
    //       ) {
    //         if (APP_CONFIG.DEBUG.MAPS)
    //           console.info("==== STREETVIEW CLOSED, SHOW PUSH PINS");
    //         updateEntitiesOnMapTypeChange(
    //           props,
    //           APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
    //             .type as TMapType
    //         ); // to show pushpins on streetside view close
    //       }
    //     }
    //     if (
    //       mapTypeId ===
    //       APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE].type
    //     ) {
    //       updateEntitiesOnMapTypeChange(
    //         props,
    //         APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE]
    //           .type as TMapType
    //       );
    //     }
    //   }
    // );
    //   map.Events.addListener(
    //     props.mapRef.current.map,
    //     "maptypechanged",
    //     (e: any) => {
    //       console.log("== MAP TYPE CHANGED", e);
    //       props.onMapTypeChange?.(e);
    //       if (
    //         e.oldMapTypeId !== e.newMapTypeId &&
    //         e.newMapTypeId ===
    //           APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].id
    //       ) {
    //         if (APP_CONFIG.DEBUG.MAPS)
    //           console.log("birdseye view closed, center to data points");
    //         updateEntitiesOnMapTypeChange(props, e.target.getMapTypeId()); // to show pushpins on birdseye view close
    //         mapCenterToDataPoints(props, checkedVehicles);
    //       }
    //       // updateEntitiesOnMapTypeChange(props, e.target.getMapTypeId())
    //     }
    //   );
    // };

    // load map modules before rendering the map objects
    // Microsoft?.Maps?.loadModule(
    //   [
    //     "google.maps.SpatialMath",
    //     "google.maps.Contour",
    //     "google.maps.Clustering",
    //   ],
    //   () => {
    //     // render the map objects
    //     renderMapObjects();
    //   }
    // );

    return null;
  };

  renderMapObjects();
};

export const mapUpdatesHandler: TMapUpdatesHandler = (props, action, value) => {
  if (APP_CONFIG.DEBUG.MAPS)
    console.log("[mapUpdatesHandler]", action, value?.length);
  // const pushpinsWithinBounds = mapGetPushpinCountWithinMapBounds(props)
  switch (action) {
    case "checkedUpdated":
      const checkedVehicles = value as string[];
      // update the visibility of the pushpins
      props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
        pushpinObject.pushpin.setMap(
          props.mapLayerOptions?.clustering ||
            !checkedVehicles.includes(pushpinObject.id)
            ? null
            : props.mapRef.current.map
        );
      });
      // update the cluster layer with the updated pushpins
      if (props.mapLayerOptions?.clustering) {
        const applicableMarkers = props.mapRef.current.objects.mPushpins
          .filter((pushpin) => checkedVehicles.includes(pushpin.id))
          .map((pushpinObject) => pushpinObject.pushpin);
        (window as any).mapClusterLayer?.clearMarkers?.();
        (window as any).mapClusterLayer?.addMarkers?.(applicableMarkers);
      }
      // update visibility of the infoboxes
      // new approach start
      const totalPushpins = props.mapRef.current.objects.mPushpins.length;
      let selectedPushpins = 0;
      props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
        const selectThisPushpin =
          checkedVehicles.includes(pushpinObject.id) &&
          checkedVehicles.length <=
            APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD;
        // console.log(selectThisPushpin ? 'YES' : 'NO', 'comparing pushpin:', pushpinObject.id, 'with checkedVehicles:', checkedVehicles);
        // pushpinObject.infobox.setOptions({
        //   visible: selectThisPushpin,
        // });
        if (selectThisPushpin) {
          pushpinObject.infobox?.open?.({
            anchor: pushpinObject.pushpin,
            map: props.mapRef.current.map,
          });
        } else {
          const alwaysOpen = props.accountSettings?.infoboxalwaysopen ?? 0;
          if (alwaysOpen !== 1) {
            pushpinObject.infobox?.close?.();
          }
        }
        if (selectThisPushpin) selectedPushpins++;
      });
      if (APP_CONFIG.DEBUG.MAPS)
        console.log(
          "totalPushpins:",
          totalPushpins,
          "selectedPushpins:",
          selectedPushpins
        );
      // new approach end
      // props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
      //   pushpinObject.infobox.setOptions({
      //     visible:
      //       checkedVehicles.includes(pushpinObject.id)
      //       && checkedVehicles.length <= APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD
      //       // && pushpinsWithinBounds <= APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD
      //   });
      // });
      (window as any).checkedVehicles = checkedVehicles;
      mapCenterToDataPoints(props, checkedVehicles, {
        manipulateInfoboxes: false,
      });
      // when no vehicles are selected the 'viewchangeend' event is not triggered, thus we need to update the infoboxes visibility here
      if (checkedVehicles.length === 0) {
        updateInfoboxesVisibility(props);
      }
      break;
    case "centerToPushpin":
      if (!!value && value.length > 0) {
        mapCenterToDataPoints(props, value as string[]);
        return;
      }
      const pushpinId = value as string;
      const pushpinObject = props.mapRef.current.objects.mPushpins.find(
        (pushpinObject) => pushpinObject.id === pushpinId
      );
      if (pushpinObject) {
        props.mapRef.current.map.setView({
          center: pushpinObject.pushpin.getLocation(),
          zoom: props.mapRef.current.map.getZoom(), // 15,
          animate: true,
        });
      }
      break;
    case "focusPushpin":
      const pushpinObj = value as { id: string; focus: boolean };
      // Taken down first, and without waiting to find the pushpin: a
      // deselect routinely arrives for an asset that is no longer on the
      // map (a refresh dropped it, a filter hid it), and the ring has to
      // come down in that case too or it is left breathing over nothing.
      if (!pushpinObj.focus) props.mapRef.current.selectedPulse?.hide();
      const pushpinObjectFocus = props.mapRef.current.objects.mPushpins.find(
        (pushpinObject) => pushpinObject.id === pushpinObj.id
      );
      if (pushpinObjectFocus) {
        // @ts-ignore: Unreachable code error
        const dataPoint = props.dataPoints.find(
          (dataPoint: TDataPoint | TAssetLocationDataPoint) =>
            getVehicleUUID(dataPoint) === pushpinObj.id
        ); // the comment is added to avoid TS error
        if (!dataPoint) return;
        // disabled as icon change is not possible with an `AdvancedMarkerElement` object
        // we are using an extra `pushpinHighlightMarker` as a workaround for highlight
        // pushpinObjectFocus.pushpin.setOptions({
        //   icon: mapVehicleIconWrapped(
        //     mapVehicleStateIconSlug(dataPoint),
        //     pushpinObj.focus,
        //     (dataPoint as TAssetLocationDataPoint)?.asset_map_status ===
        //       ASSET_MAP_STATUS.IGNITION_OFF
        //       ? 45 // TODO: need to fix why 45 needs to be provided
        //       : mapVehicleStateIconAngle(dataPoint)
        //   ),
        // });
        if (pushpinObj.focus) {
          // Remember where the map was before the first asset was focused.
          // Only the first: stepping from one asset to the next is still the
          // same visit, and saving each time would make "back" mean the
          // previous asset at ZOOM_MAX rather than the view you came from.
          if (!props.mapRef.current.viewBeforeFocus) {
            const centre = props.mapRef.current.map?.getCenter?.();
            const zoom = props.mapRef.current.map?.getZoom?.();
            if (centre && typeof zoom === "number")
              props.mapRef.current.viewBeforeFocus = {
                center: { lat: centre.lat(), lng: centre.lng() },
                zoom,
              };
          }
          const zoomLevel = APP_CONFIG.MAPS.ZOOM_MAX; // props.mapRef.current.map.getZoom() + 1; // Math.min(8, props.mapRef.current.map.getZoom() + 1);
          props.mapRef.current.map.setOptions({
            center: {
              lat: pushpinObjectFocus.pushpin.position.lat,
              lng:
                pushpinObjectFocus.pushpin.position.lng +
                0.0001 * Math.pow(2, 15 - zoomLevel), // shift the center a bit to show the infobox
            },
            zoom: zoomLevel,
            animate: true,
          });
          // The zoom alone says where the map went, not which pin was
          // picked - at ZOOM_MAX over a yard full of assets that is the
          // whole question. Same ring the asset map puts under a selected
          // event, so the two screens answer it the same way.
          //
          // Positioned on the pushpin itself, NOT on the centre above: that
          // one is nudged sideways to make room for the infobox, and a ring
          // sitting off to one side of its pin looks like a second marker.
          const position = pushpinObjectFocus.pushpin.position;
          const latitude =
            typeof position?.lat === "function" ? position.lat() : position?.lat;
          const longitude =
            typeof position?.lng === "function" ? position.lng() : position?.lng;
          if (typeof latitude === "number" && typeof longitude === "number") {
            if (!props.mapRef.current.selectedPulse) {
              props.mapRef.current.selectedPulse = createSelectedEventPulse(
                props.mapRef.current.map
              );
            } else {
              // A refresh takes the map component down and brings a brand
              // new google.maps.Map back, so a ring held over from before
              // it is pointing at a map that no longer exists - it has to
              // be moved onto the live one or it never reappears.
              props.mapRef.current.selectedPulse.attachTo(
                props.mapRef.current.map
              );
            }
            props.mapRef.current.selectedPulse.showAt({ latitude, longitude });
          }
        }
        // update background highlight marker
        // pushpinObjectFocus?.pushpinHighlightMarker?.setMap(
        //   pushpinObj.focus ? props.mapRef.current.map : null
        // );
        if (!!pushpinObjectFocus?.pushpin) {
          pushpinObjectFocus.pushpin.content = pushpinObj.focus
            ? pushpinObjectFocus.markerSvgs.highlighted
            : pushpinObjectFocus.markerSvgs.regular;
        }
      }
      break;
    // Closing the Details panel. Focusing an asset drives the map to
    // ZOOM_MAX over one pin, so the way back is the view it was driven from
    // - not a fit of every checked vehicle, which is the whole fleet and
    // tells you nothing about where you were.
    case "restorePreFocusView":
      const savedView = props.mapRef.current.viewBeforeFocus;
      if (savedView) {
        props.mapRef.current.viewBeforeFocus = undefined;
        props.mapRef.current.map.setOptions({
          center: savedView.center,
          zoom: savedView.zoom,
          animate: true,
        });
        return;
      }
      // nothing was saved - the selection came from a deep link, say - so
      // fall back to framing what is checked rather than sitting at ZOOM_MAX
      mapCenterToDataPoints(props, value as string[]);
      break;
    case "centerToCoords":
      const coords = value as { center: number[]; zoom: number };
      if (coords.center.length === 2) {
        // props.mapRef.current.map.setView({
        //   center: { lat: coords.center[0], lng: coords.center[1] },
        //   zoom: coords.zoom ?? 5,
        //   animate: true,
        // });
        props.mapRef.current.map.setOptions({
          center: {
            lat: coords.center[0],
            lng: coords.center[1],
          },
          zoom: coords.zoom ?? 5,
          animate: true,
        });
      }
      break;
    default:
      break;
  }
};

// center the map to the visible pushpins
const mapCenterToDataPoints: TMapOperations = async (
  props,
  value,
  options = { manipulateInfoboxes: true }
) => {
  if (!props.dataPoints || props.dataPoints.length < 1) return;
  if (APP_CONFIG.DEBUG.MAPS)
    console.log("[mapCenterToDataPoints] called ---------------------------");

  const checkedVehicles = value as string[];
  // @ts-ignore: Unreachable code error
  const visibleDataPoints = props.dataPoints.filter(
    (dataPoint: TDataPoint | TAssetLocationDataPoint) =>
      checkedVehicles.includes(getVehicleUUID(dataPoint)) &&
      mapVehicleHasValidCoords(dataPoint)
  ); // the comment is added to avoid TS error

  const google = (window as any).google;
  if (visibleDataPoints.length < 1) {
    if (APP_CONFIG.DEBUG.MAPS)
      console.log("[mapCenterToDataPoints] zero visible points");
    // if visible data points are not available, zoom out to world map
    props.mapRef.current.map.setOptions({
      center: new google.maps.LatLng(
        APP_CONFIG.MAPS.DEFAULTS.CENTER.latitude,
        APP_CONFIG.MAPS.DEFAULTS.CENTER.longitude
      ),
    });
    props.mapRef.current.map.setZoom(APP_CONFIG.MAPS.DEFAULTS.ZOOM);
    return;
  }

  // Set bounds to map
  if (APP_CONFIG.DEBUG.MAPS)
    console.log(
      `[mapCenterToDataPoints] A. ${visibleDataPoints.length} visible points`
    );
  setTimeout(() => {
    if (APP_CONFIG.DEBUG.MAPS)
      console.log(
        `[mapCenterToDataPoints] B. ${visibleDataPoints.length} visible points`
      );
    const bounds = new google.maps.LatLngBounds();
    visibleDataPoints.forEach(
      (dataPoint: TDataPoint | TAssetLocationDataPoint) => {
        const lat =
          (dataPoint as TDataPoint)?.lastvalidlatitude ??
          (dataPoint as TAssetLocationDataPoint)?.eventdata?.latitude;
        const lng =
          (dataPoint as TDataPoint)?.lastvalidlongitude ??
          (dataPoint as TAssetLocationDataPoint)?.eventdata?.longitude;
        bounds.extend({ lat, lng });
      }
    );
    props.mapRef.current?.map?.fitBounds?.(bounds, 50); // Adjust padding as needed
  }, 4 * 200); // changed '200' to '4 * 200' to accommodate any delay in identifying/rendering markers in case of large number of markers
  // show the infobox if there is only 6 (APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD) or less visible pushpins
  // const pushpinsWithinBounds = mapGetPushpinCountWithinMapBounds(props)
  if (options.manipulateInfoboxes) {
    const alwaysOpen = props.accountSettings?.infoboxalwaysopen ?? 0;
    if (
      visibleDataPoints.length <= APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD
      // && pushpinsWithinBounds <= APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD
    ) {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("MODE-A showing infoboxes for visible pushpins");
      // visibleDataPoints.forEach((dataPoint: TDataPoint | TAssetLocationDataPoint) => {
      //   const pushpinObject = props.mapRef.current.objects.mPushpins.find((pushpinObject) => pushpinObject.id === getVehicleUUID(dataPoint));
      //   if (pushpinObject) {
      //     pushpinObject.infobox.setOptions({
      //       visible: true
      //     });
      //   }
      // });
      props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
        pushpinObject.infobox.setOptions({
          visible: false,
        });
      });
    } else {
      if (APP_CONFIG.DEBUG.MAPS) console.log("MODE-B hiding all infoboxes");
      props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
        // pushpinObject.infobox.setOptions({
        //   visible: false,
        // });
        if (alwaysOpen !== 1) {
          pushpinObject?.infobox?.close();
        }
      });
    }
  }
  // limit the zoom level to 7
  if (
    visibleDataPoints.length === 1 &&
    props.mapRef.current.map?.getZoom() > 7
  ) {
    props.mapRef.current.map.setOptions({
      zoom: 7,
    });
  }
};

// helper function to check if vehicle has valid coordinates
export const mapVehicleHasValidCoords = (
  vehicle: TDataPoint | TAssetLocationDataPoint | undefined
) => {
  return (
    !!vehicle &&
    !!(
      (vehicle as TDataPoint)?.lastvalidlatitude ||
      (vehicle as TAssetLocationDataPoint)?.eventdata?.latitude
    ) &&
    !!(
      (vehicle as TDataPoint)?.lastvalidlongitude ||
      (vehicle as TAssetLocationDataPoint)?.eventdata?.longitude
    )
  );
};

// helper function to check if vehicle is active
export const mapVehicleIsActive = (
  dataPoint: TDataPoint | TAssetLocationDataPoint | undefined
) => {
  return (
    !!dataPoint &&
    dataPoint?.isactive === 1 &&
    mapVehicleHasValidCoords(dataPoint)
  );
};

export const mapVehicleOrientation = (
  dataPoint: TDataPoint | TAssetLocationDataPoint | undefined
) => {
  return !!dataPoint &&
    ((dataPoint as TDataPoint)?.lastvalidheading !== null ||
      (dataPoint as TAssetLocationDataPoint)?.eventdata?.heading !== null) &&
    ((dataPoint as TDataPoint)?.lastvalidheading > 0 ||
      (dataPoint as TAssetLocationDataPoint)?.eventdata?.heading > 0)
    ? ((dataPoint as TDataPoint)?.lastvalidheading ??
        (dataPoint as TAssetLocationDataPoint)?.eventdata?.heading)
    : 0;
};

// helper function to map vehicle state icon slug
export const mapVehicleStateIconSlug = (
  dataPoint: TDataPoint | TAssetLocationDataPoint | undefined
) => {
  // if (mapVehicleOrientation(dataPoint) > 0) return 'driving';
  // return mapVehicleIsActive(dataPoint) ? 'idle_active' : 'idle_inactive';
  switch ((dataPoint as TAssetLocationDataPoint)?.asset_map_status) {
    case ASSET_MAP_STATUS.DRIVING:
      return "driving";
    case ASSET_MAP_STATUS.IDLE:
      return "idle_active";
    case ASSET_MAP_STATUS.IGNITION_OFF:
      return "ignition_off";
    case ASSET_MAP_STATUS.NOT_REPORTING:
    default:
      return "idle_inactive";
  }
};

// helper function to map vehicle state icon angle
const mapVehicleStateIconAngle = (
  dataPoint: TDataPoint | TAssetLocationDataPoint
) => {
  const vehicleHeadingAngle = mapVehicleOrientation(dataPoint);
  return vehicleHeadingAngle;

  // the code below is deprecated
  // const Microsoft = (window as any).Microsoft;
  // let angle = 0;
  // if(dataPoint.coords.length > 2) { // TODO: dissolve this check later and use OranizationVehicle's lat and lon
  //   // calculate angle for the vehicle icon
  //   const point1 = { x: dataPoint.coords[0], y: dataPoint.coords[1] }
  //   const point2 = { x: dataPoint.coords[2], y: dataPoint.coords[3] }
  //   angle = Math.atan2(point2.y - point1.y, point2.x - point1.x) * 180 / Math.PI;
  // }
  // return angle;
};

const mapGetPushpinCountWithinMapBounds = (props: TMapOperationsProps) => {
  const checkedVehicles = (window as any).checkedVehicles ?? [];
  const mapBounds = props.mapRef?.current?.map?.getBounds();
  if (!mapBounds) return 0;
  const pushpins = props.mapRef.current.objects.mPushpins;
  return pushpins.filter(
    (pushpinObject) =>
      checkedVehicles.includes(pushpinObject.id) &&
      mapPushpinLiesWithinMapBounds(
        pushpinObject.pushpin,
        props.mapRef?.current?.map
      )
  ).length;
};

const mapPushpinLiesWithinMapBounds = (pushpin: any, mapObj: any) => {
  const mapBounds = mapObj.getBounds();
  if (!mapBounds) return false;
  return mapBounds.contains(pushpin.getLocation()) && pushpin.getVisible();
};

const updateInfoboxesVisibility = (props: TMapOperationsProps) => {
  const checkedVehicles = (window as any).checkedVehicles ?? [];
  const pushpinCountWithinMapBounds = mapGetPushpinCountWithinMapBounds(props);
  const alwaysOpen = props.accountSettings?.infoboxalwaysopen ?? 0;
  if (APP_CONFIG.DEBUG.MAPS)
    console.log("pushpinCountWithinMapBounds:", pushpinCountWithinMapBounds);
  const showInfoboxes =
    pushpinCountWithinMapBounds <=
    APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD;
  if (APP_CONFIG.DEBUG.MAPS)
    console.log(
      `[updateInfoboxesVisibility] viewchangeend count: ${pushpinCountWithinMapBounds} showInfoboxes: ${showInfoboxes}`
    );

  // update mapState with new center and zoom
  if (!!props.mapRef.current.map?.getCenter) {
    props?.onViewChangeEnd?.(
      {
        latitude: props.mapRef.current.map?.getCenter().latitude,
        longitude: props.mapRef.current.map?.getCenter().longitude,
      },
      props.mapRef.current.map.getZoom()
    );
  }

  if (showInfoboxes) {
    // show infoboxes for pushpins within the map bounds
    props.mapRef.current.objects.mPushpins
      .filter((pushpinObject) => {
        const loc = new google.maps.LatLng(
          pushpinObject.pushpin.position.lat,
          pushpinObject.pushpin.position.lng
        ); // pushpinObject.pushpin.getLocation();
        return (
          props.mapRef.current?.map?.getBounds?.()?.contains?.(loc) || false
        );
      })
      .forEach((pushpinObject) => {
        // setTimeout(() => {
        // pushpinObject.infobox.setOptions({
        //   // true
        //   visible: false,
        //   // checkedVehicles.includes(pushpinObject.id) &&
        //   // mapPushpinLiesWithinMapBounds(
        //   //   pushpinObject.pushpin,
        //   //   props.mapRef.current.map
        //   // ),
        //   // pushpinObject.pushpin.getVisible() &&
        //   // props.mapRef.current.objects.mPushpins.filter((pushpinObject) => {
        //   //   const loc = pushpinObject.pushpin.getLocation();
        //   //   return props.mapRef.current.map.getBounds().contains(loc);
        //   // }).length <= APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD
        // });
        if (alwaysOpen !== 1) {
          // Only close if not always open
          pushpinObject.infobox?.close?.();
        }
        // }, 100);
      });
  } else {
    // hide all infoboxes
    if (alwaysOpen !== 1) {
      // Only close if not always open
      props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
        pushpinObject.infobox?.close?.();
      });
    }
  }
};

export const updateEntitiesOnMapTypeChange = (
  props: TMapOperationsProps,
  mapType: TMapType
) => {
  if (APP_CONFIG.DEBUG.MAPS)
    console.log("[updateEntitiesOnMapTypeChange]", mapType);
  const pushpinCountWithinMapBounds = mapGetPushpinCountWithinMapBounds(props);
  const showInfoboxes =
    pushpinCountWithinMapBounds <=
    APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD;
  // hide infoboxes when map type is streetside / birdseye
  if (APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(mapType)) {
    props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
      pushpinObject.infobox.setOptions({
        visible: false,
      });
    });
  } else {
    updateInfoboxesVisibility(props as TMapOperationsProps);
  }
  // props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
  //   pushpinObject.infobox.setOptions({
  //     visible: showInfoboxes && !APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(mapType) // (mapType !== APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.STREETSIDE].type)
  //   });
  // });
  // hide clusters when map type is streetside / birdseye
  props.mapRef.current.objects.mClusterLayer?.setOptions({
    visible: !APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(mapType), // (mapType !== APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.STREETSIDE].type)
  });
};

// TODO: selecting / deselecting vehicles is casuing blinking of infoboxes (around 6 pushpins)
